import React, { useState } from "react";
import {
    Box,
    Card,
    TextField,
    Typography,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setAdminCredentials } from "../../features/admin/authSlice.ts";

const AdminLogin = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Test credentials
    const testCredentials = {
        email: "balaksara@yahoo.com",
        password: "Test@1234"
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAdminLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        // Frontend validation
        if (!email.trim()) {
            setErrorMsg("Email is required");
            setLoading(false);
            return;
        }

        if (!validateEmail(email)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        if (!password.trim()) {
            setErrorMsg("Password is required");
            setLoading(false);
            return;
        }

        
        const loginURL = `${process.env.REACT_APP_SERVER_ENDPOINT}/admin/login`;
        const authkey = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

        const requestBody = {
            email: email.trim(),
            password: password
        };

        try {
            const response = await fetch(loginURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "authkey": authkey,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('404 Not Found: The API endpoint was not found.');
                } else if (response.status === 403) {
                    throw new Error('403 Forbidden: Invalid authkey or permissions.');
                } else if (response.status === 401) {
                    throw new Error('401 Unauthorized: Invalid credentials.');
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }

            const result = await response.json();

            if (!result.error && result.token) {
                setSuccessMsg("Login successful! Redirecting...");
                
                // Decode the JWT token to get user info
                const token = result.token;
                const tokenParts = token.split('.');
                if (tokenParts.length === 3) {
                    try {
                        const payload = JSON.parse(atob(tokenParts[1]));
                        const userId = payload.id;
                        const userEmail = payload.email;
                        const userRole = payload.role || 'admin'; // Get role from token
                        
                        // Store admin credentials in localStorage
                        localStorage.setItem("admin_token", token);
                        localStorage.setItem("admin_type", userRole); // Store admin type from token
                        localStorage.setItem("admin_email", userEmail);
                        localStorage.setItem("admin_id", userId.toString());
                        localStorage.setItem("admin_name", userEmail.split('@')[0]); // Use email prefix as name
                        
                        // Dispatch Redux action to store admin credentials
                        dispatch(setAdminCredentials({
                            token: token,
                            email: userEmail,
                            userId: userId.toString(),
                            userName: userEmail.split('@')[0],
                            role: userRole, // Store the actual role
                            adminType: userRole // Store as adminType
                        }));
                        
                        setTimeout(() => {
                            setSuccessMsg("");
                            navigate("/adminDashboard");
                        }, 2000);
                    } catch (decodeError) {
                        console.error("Error decoding token:", decodeError);
                        // Fallback if token decode fails
                        localStorage.setItem("admin_token", token);
                        localStorage.setItem("admin_type", "admin");
                        localStorage.setItem("admin_email", email.trim());
                        dispatch(setAdminCredentials({
                            token: token,
                            email: email.trim(),
                            userId: "",
                            userName: "Admin",
                            role: "admin",
                            adminType: "admin"
                        }));
                        setTimeout(() => {
                            setSuccessMsg("");
                            navigate("/adminDashboard");
                        }, 2000);
                    }
                } else {
                    // Invalid token format
                    setErrorMsg("Invalid token received from server");
                    setLoading(false);
                }
            } else {
                setErrorMsg(result.message || "Please check the email and password");
                setSuccessMsg("");
                setLoading(false);
            }
        } catch (error: any) {
            console.error("Login error:", error);
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                setErrorMsg("Network error: Unable to connect to server. Please check your connection.");
            } else if (error.message.includes('HTTP error')) {
                setErrorMsg(`Server error: ${error.message}`);
            } else {
                setErrorMsg(`Login failed: ${error.message}`);
            }
            setSuccessMsg("");
            setLoading(false);
        }
    };

    const fillTestCredentials = () => {
        setEmail(testCredentials.email);
        setPassword(testCredentials.password);
        setErrorMsg("");
        setSuccessMsg("");
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
                padding: 2,
            }}
        >
            <Card
                sx={{
                    width: 450,
                    padding: 4,
                    boxShadow: 4,
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        color: "#1976d2",
                        fontWeight: "bold",
                        mb: 3,
                    }}
                >
                    Admin Login
                </Typography>

                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                <form onSubmit={handleAdminLogin}>
                    <TextField
                        label="Email Address"
                        type="email"
                        fullWidth
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your email address"
                    />

                    <TextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 3 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your password"
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            py: 1.5,
                            fontSize: "1.1rem",
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Signing In...
                            </>
                        ) : (
                            "Sign In"
                        )}
                    </Button>

                    <Button
                        onClick={fillTestCredentials}
                        variant="outlined"
                        fullWidth
                        sx={{
                            mt: 2,
                            textTransform: "none",
                            color: "#666",
                            borderColor: "#ddd",
                        }}
                    >
                        Fill Test Credentials
                    </Button>
                </form>
            </Card>
        </Box>
    );
};

export default AdminLogin;